import { AxiosRequestConfig } from 'axios';
import { Centrifuge, Subscription } from 'centrifuge';

import { IRestResponse } from './axios';
import { processResponse } from './helpers';

const RESPONSE_TIMEOUT = 50000;
const LONG_RESPONSE_TIMEOUT = 3000;

interface IRequest {
	resolve: (result: any) => void;
	reject: any;
	timeOut: ReturnType<typeof setTimeout>;
	longResponseTimeOut: ReturnType<typeof setTimeout>;
}

interface IUnsubscribedResponse {
	data: any;
	timeOut: ReturnType<typeof setTimeout>;
}

export class Client {
	// @TODO: Memory leak. Cleanup needed
	_sub: Subscription;

	_client: Centrifuge;

	_requests: Record<string, IRequest> = {};

	_unsubscribed: Record<string, IUnsubscribedResponse> = {};

	constructor(_: string) {
		// this._client = new Centrifuge(endpoint);
		this._requests = {};
		this._unsubscribed = {};
	}

	static _logLongResponse(time: number, config: AxiosRequestConfig, response: IRestResponse) {
		const longResponses = JSON.parse(localStorage.getItem('longResponses')) || [];
		longResponses.push({ time, config, response });
		localStorage.setItem('longResponses', JSON.stringify(longResponses));
		console.info('Long Response', { time, config, response });
	}

	static async _process(resolve: any, reject: any, data: any) {
		try {
			const result = await processResponse(data);
			resolve(result.data);
		} catch (e) {
			reject(e);
		}
	}

	_timeOut(requestId: string, reject: (reason: any) => void) {
		delete this._requests[requestId];
		reject('timeout');
	}

	async connect(token: string, channel: string) {
		this._client.setToken(token);
		await this._client.connect();
		this._sub = this._client.newSubscription(channel);
		await this._sub.subscribe();
		this._sub.on('publication', async (ctx) => {
			const requestId = ctx.data.message.request_id;
			if (requestId in this._requests) {
				const request = this._requests[requestId];
				await Client._process(request.resolve, request.reject, ctx.data);
				clearTimeout(request.timeOut);
				clearTimeout(request.longResponseTimeOut);
				delete this._requests[requestId];
			} else {
				console.info(`Message without subscription id: ${requestId}`, ctx.data);
				const timeOut = setTimeout(() => {
					delete this._unsubscribed[requestId];
				}, RESPONSE_TIMEOUT);
				this._unsubscribed[requestId] = {
					data: ctx.data,
					timeOut,
				};
			}
		});
	}

	async disconnect() {
		if (this._sub) {
			this._sub.unsubscribe();
			this._sub.removeAllListeners();
			this._client.removeSubscription(this._sub);
		}
		this._client.disconnect();
	}

	async subscribe(request_id: string, config: AxiosRequestConfig, response: IRestResponse) {
		const time = Date.now();
		return new Promise((resolve, reject) => {
			if (request_id in this._unsubscribed) {
				Client._process(resolve, reject, this._unsubscribed[request_id].data);
				clearTimeout(this._unsubscribed[request_id].timeOut);
				delete this._unsubscribed[request_id];
			} else {
				const longResponseTimeOut = setTimeout(
					() => Client._logLongResponse(time, config, response),
					LONG_RESPONSE_TIMEOUT,
				);
				const timeOut = setTimeout(() => {
					this._timeOut(request_id, reject);
				}, RESPONSE_TIMEOUT);
				this._requests[request_id] = {
					resolve,
					reject,
					timeOut,
					longResponseTimeOut,
				};
			}
		});
	}
}

export default new Client('');
